import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, fromEvent, interval, map, merge, Observable, of, Subscription } from 'rxjs';
import { ApiService } from '../lib/services/api.service';
import { AppService } from '../lib/services/app.service';
import { Me } from '../lib/services/me.service';
import { MatNotificationsComponent } from 'src/lib/modules/mat-notifications/mat-notifications.module';
import { heightAnimation, iconExpandAnimation } from 'src/lib/animations';
import { ApiSocketService } from 'src/lib/services/api.socket.service';
import { SwUpdate } from '@angular/service-worker';
import { isAuthenticatedGuard } from '@lib/guards/authenticated.guard';
import { FormControl } from '@angular/forms';
import { SearchIndex, SearchService } from '@lib/services/search.service';

enum UpdateType {
	None = 0,
	Me = 1,
	Application = 2,
}

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	animations: [heightAnimation, iconExpandAnimation]
})
export class AppComponent implements OnInit, AfterViewInit {

	UpdateType = UpdateType;

	subscriptions: Subscription = new Subscription();

	menu: Observable<any>
	
	currentSearch: any = {id: "search", children: null}
	currentSupport: any = {id: "support"}
	currentMessages: any = {id: "messages", children: null}
	currentOffline: any = {id: "offline", isOffline: false}
	currentUpdateAvailable: any = {id: "update-available", type: UpdateType.None}
	currentItem: any
	currentMainItem: any
	currentMainSubItem: any

	searchControl: FormControl<string> = new FormControl();

	version: string = this.appService.environment.version;
	isBeta: boolean = this.appService.isBeta;
	isDevelopment: boolean = this.appService.isDevelopment;

	canActivatePublic: boolean = this.canPublicActivate(location.pathname);

	isUpdateAvailable: boolean = false;
	isUpdateAvailableMore: boolean = true;

	private disconnecting: boolean = false;
    @HostBinding('class.app-framed') isFramed: boolean = this.appService.isFramed;

	@HostListener('window:focus') onFocus(): void {

        if(this.isAuthenticated && /^\/login/.test(location.pathname))
            this.router.navigate([this.activatedRoute.snapshot.queryParams.return ?? "/"], {replaceUrl: true});

        if(!this.canPublicActivate(location.pathname))
            this.authenticatedGuard.canActivate(this.activatedRoute.snapshot, this.router.routerState.snapshot);
        
		if(this.swUpdate.isEnabled)
			this.swUpdate.checkForUpdate();
    }

    @ViewChild('sidenavContentElement', {read: ElementRef}) sidenavContentElementRef: ElementRef;
	@ViewChild('toolbarElement', {read: ElementRef}) toolbarElementRef: ElementRef;
	@ViewChild('sidenav') sidenavRef: MatSidenav;

	constructor(public window: Window,
				public me: Me,
				private apiService: ApiService,
				private apiSocketService: ApiSocketService,
				private appService: AppService,
				private searchService: SearchService,
				private activatedRoute: ActivatedRoute,
				private authenticatedGuard: isAuthenticatedGuard,
				private router: Router,
				private overlay: Overlay,
				private swUpdate: SwUpdate,
				private changeDetectorRef: ChangeDetectorRef) {

		/**
		 * Notifications
		 */
        const notificationsOverlayRef: OverlayRef = this.overlay.create({positionStrategy: this.overlay.position().global().centerHorizontally().bottom()});
        notificationsOverlayRef.attach(new ComponentPortal(MatNotificationsComponent));
        notificationsOverlayRef.hostElement.style.zIndex = '1002';

		/**
		 * On authenticated
		 */
		this.apiService.onAuthenticated().subscribe(authenticated => {
			if(authenticated) {

				this.menu = this.apiService.get('menu', {shared: true});

				this.apiService.get('messages').subscribe(messages => this.updateMessages(messages));

				/**
				 * Lives updates subscriptions
				 * User, messages updates
				 */
				this.apiSocketService.connect();

				this.subscriptions.add(
					this.apiSocketService.fromEvent('messages').subscribe((messages: any[]) => this.updateMessages(messages))
				);

				this.subscriptions.add(
					this.apiSocketService.fromEvent('user').subscribe((event: {me, token}) => this.me.update.next(event))
				);
			}
		});

		/**
		 * On navigate
		 */
		 this.appService.onNavigate().subscribe(state => {

			if(this.sidenavRef.opened)
				this.sidenavRef.close();

			if(this.currentMainItem && this.currentMainItem.id !== 'messages')
				this.currentMainItem = null;

			if(this.messages)
				this.updateMessages(this.messages);

			this.canActivatePublic = this.canPublicActivate(state.url);
		});

		/**
		 * On Search
		 */
		this.searchControl.valueChanges
        .pipe(this.searchService.search())
		.subscribe(({index, response, isBeforeRequest, isFirst}) => {

			if(isBeforeRequest && isFirst)
				delete this.currentSearch.children;

			if(!isBeforeRequest && response?.length > 0) {

				if(!this.currentSearch.children)
					this.currentSearch.children = [];

				switch(index) {
					case SearchIndex.Users:
						response = response.map(item => ({
							...item,
							title: item.nomComplet,
							description: [item.fonctionAndVille],
							link: `profile/${item.id ?? item.guid}`
						}));
						
						response = {
							id: 'users',
							order: 0,
							children: [{
								name: 'Collaborateurs',
								icon: 'people_alt',
								children: response.slice(0, 7),
                                childrenMore: {
									label: 'Plus de résultats',
									link: ['/search/users/', this.searchControl.value] 
								}
							}]
						};
						break;

					case SearchIndex.Pages:
						response = {
							order: 2,
							children: [{
								name: 'Pages du portail',
								icon: 'web',
								children: response.map(item => ({
									...item,
									description: item.ancestors.map(i => i.name).join(' > ')
								}))
							}]
						};
						break;

					case SearchIndex.Etablissements:
						response = response.map(item => ({
							...item,
							title: item.nom,
							description: [item.adresse, `${item.cp} ${item.ville}`],
							link: `profile/etablissement/${item.id}`,
                            icon: 'apartment'
						}));
						response = {
							id: 'etablissements',
							order: 1,
							children: [{
								name: 'Etablissements',
								icon: 'apartment',
								children: response.slice(0, 7),
                                childrenMore: {
									label: 'Plus de résultats',
									link: ['/search/etablissements/', this.searchControl.value] 
								} 
							}]
						};
						break;

					case SearchIndex.Annuaire:
						response = {
							order: 4,
							children: [{
								name: 'Annuaire',
								icon: 'contacts',
								children: response
							}]
						}
						break;

					case SearchIndex.Drive:
						response = {
							order: 3,
							children: [{
								name: 'Documents',
								icon: 'folder',
								children: response
                                    .slice(0, 7)
                                    .map(item => ({
                                        name: item.name,
                                        description: item.breadcrumbs.map(i => i.name).slice(0, -1).join(' > '),
                                        link: item.breadcrumbs.slice(0, -1).last?.link,
                                        icon: item.folder ? 'folder' : 'description'
                                    })),
								childrenMore: {
									label: 'Plus de résultats',
									link: ['/drive/search', this.searchControl.value] 
								} 
							}]
						}
						break;
				}

				this.currentSearch.children = [...this.currentSearch.children, response].sort((a, b) => a.order - b.order);
			}
		});

		/**
		 * On update available
		 */
		this.subscriptions.add(
			this.me.onUpdate.subscribe(() => {
				this.currentUpdateAvailable = {...this.currentUpdateAvailable, type: UpdateType.Me};
				this.isUpdateAvailable = true;
				this.isUpdateAvailableMore = false;
			})	
		);

		this.subscriptions.add(
			this.swUpdate.versionUpdates.pipe(filter(event => event.type === 'VERSION_READY')).subscribe(() => {
				this.currentUpdateAvailable = {...this.currentUpdateAvailable, type: UpdateType.Application};
				this.isUpdateAvailable = true;
				this.isUpdateAvailableMore = true;
			})
		);

		/**
		 * On offline
		 */
		this.subscriptions.add(
			merge(
				of(null),
				fromEvent(window, 'online'),
				fromEvent(window, 'offline')
			)
			.pipe(map(() => navigator.onLine))
			.subscribe(isOnline => {
				this.currentOffline.isOffline = !isOnline;

				if(isOnline)
					this.apiSocketService.connect();
				else
					this.apiSocketService.disconnect();
			})
		);
	}

	get isAuthenticated(): boolean { return this.me.isAuthenticated; };

	get searchState () {return this.searchService.state}

	get currentNeededMainItem (): string | null {

		if(this.currentOffline.isOffline)
			return this.currentOffline.id;

		else if(this.isUpdateAvailable)
			return this.currentUpdateAvailable.id;

		else
			return this.currentMainItem?.id ?? null;
	}

	private messages: any[];
	private updateMessages (messages: any[]) {

		this.messages = messages;

		const path = this.router.getCurrentNavigation()?.finalUrl?.toString() ?? location.pathname;
		const filteredMessages = messages.filter(({page}) => new RegExp(`^${page ?? '.*'}$`, 'g').test(path));

		if(messages.length > 0) {
			if(filteredMessages.crc32() !==  this.currentMessages.children?.crc32()) {
				if(filteredMessages.length > 0) {
					this.currentMessages.children = filteredMessages;
					this.currentMainItem = this.currentMessages;
				} else {
					this.currentMessages.children = null;
					this.currentMainItem = null;
				}
			}
		}
		else if(this.currentMessages.children !== null) {
			this.messages = null;
			this.currentMessages.children = null;
			this.currentMainItem = null;
		}
	}

	ngOnInit(): void {
		this.changeDetectorRef.detectChanges();
	}

    ngAfterViewInit(): void {
        
        /**
         * Check logged interval
         */
        this.subscriptions.add(
            interval(900000).subscribe(() => {
                if(!this.canActivatePublic && !this.disconnecting && !this.me.isAuthenticated) {
                    this.disconnecting = true;
                    this.deconnect();
                }
            })
        );

        this.subscriptions.add(
            fromEvent(this.sidenavContentElementRef.nativeElement, 'scroll').subscribe(() => {
                
                const element = this.sidenavContentElementRef.nativeElement;
            
                element.style.setProperty('--scroll-top-05', `${element.scrollTop * .5}px`);
            })
        );
    }

	canPublicActivate (path: string): boolean {
		return /^\/login/.test(path) ||
			   /^\/aide/.test(path) ||
			   /^\/fail/.test(path) ||
			   /^\/profile\/password/.test(path) ||
			   /^\/medico-social-fiches-signalement/.test(path);
	}

	openSidenav (event: MouseEvent) {
		event.stopPropagation();
		this.sidenavRef.open();
	}

	toggleMenu (item: any) {
		this.currentMainItem = (this.currentMainItem && this.currentMainItem.id === item.id ? null : item);
	}

	closeMenu () {
		if(this.currentMainItem?.id !== 'messages')
			this.currentMainItem = null;
	}

	closeAll () {
		this.currentMainItem = null;

		if(this.sidenavRef.opened)
			this.sidenavRef.close();
	}

	personate () {
		this.currentMainItem = null;
		this.subscriptions.unsubscribe();
		this.apiSocketService.disconnect();
		this.me.personate();
		this.window.location.reload();
	}
	
	impersonate (user: {guid: string, nomComplet: string}) {
		this.currentMainItem = null;
		this.subscriptions.unsubscribe();
		this.apiSocketService.disconnect();
		this.me.impersonate(user).subscribe(() => this.window.location.reload())
	}

	deconnect () {
		this.subscriptions.unsubscribe();
		this.apiSocketService.disconnect();
		this.apiService.deconnect();
		this.authenticatedGuard.canActivate(this.activatedRoute.snapshot, this.router.routerState.snapshot);
		this.disconnecting = false;
	}

	update () {
		if(this.swUpdate.isEnabled)
			this.swUpdate.activateUpdate().then(() => this.window.location.reload());
		else
			this.window.location.reload();
	}

	showNextChildren(_categoryMenuItem) {
		
	}
}
